import { useState, useEffect } from 'react';
import { X, Plus, Trash2, Save, FileText, ToggleLeft, ToggleRight, AlertCircle, CheckCircle, HelpCircle } from 'lucide-react';
import apiClient from '../api/client';

interface RequiredDocument {
  id: string;
  name: string;
  isRequired: boolean;
}

interface RequiredField {
  id: string;
  label: string;
  type: string;
  isRequired: boolean;
}

interface ManualClaimConfig {
  requiredDocuments: RequiredDocument[];
  requiredFields: RequiredField[];
}

interface Props {
  companyId: string;
  companyName: string;
  onClose: () => void;
}

export default function ManualClaimConfigModal({ companyId, companyName, onClose }: Props) {
  const [config, setConfig] = useState<ManualClaimConfig>({ requiredDocuments: [], requiredFields: [] });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  
  const [activeTab, setActiveTab] = useState<'documents' | 'fields'>('documents');

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await apiClient.get<ManualClaimConfig>(`/admin/companies/${companyId}/manual-claim-config`);
        setConfig({
          requiredDocuments: res.data?.requiredDocuments || [],
          requiredFields: res.data?.requiredFields || []
        });
      } catch {
        setConfig({ requiredDocuments: [], requiredFields: [] });
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [companyId]);

  // Documents
  const addDocument = () => {
    const newDoc: RequiredDocument = {
      id: `doc_${Date.now()}`,
      name: '',
      isRequired: true,
    };
    setConfig(prev => ({ ...prev, requiredDocuments: [...prev.requiredDocuments, newDoc] }));
  };

  const updateDocument = (index: number, field: keyof RequiredDocument, value: any) => {
    setConfig(prev => ({
      ...prev,
      requiredDocuments: prev.requiredDocuments.map((doc, i) =>
        i === index ? { ...doc, [field]: value } : doc
      ),
    }));
  };

  const removeDocument = (index: number) => {
    setConfig(prev => ({
      ...prev,
      requiredDocuments: prev.requiredDocuments.filter((_, i) => i !== index),
    }));
  };

  // Fields (Questions)
  const addField = () => {
    const newField: RequiredField = {
      id: `field_${Date.now()}`,
      label: '',
      type: 'text',
      isRequired: true,
    };
    setConfig(prev => ({ ...prev, requiredFields: [...prev.requiredFields, newField] }));
  };

  const updateField = (index: number, key: keyof RequiredField, value: any) => {
    setConfig(prev => ({
      ...prev,
      requiredFields: prev.requiredFields.map((field, i) =>
        i === index ? { ...field, [key]: value } : field
      ),
    }));
  };

  const removeField = (index: number) => {
    setConfig(prev => ({
      ...prev,
      requiredFields: prev.requiredFields.filter((_, i) => i !== index),
    }));
  };

  const handleSave = async () => {
    // Validate
    if (config.requiredDocuments.some(d => !d.name.trim())) {
      setError('All document entries must have a name.');
      setActiveTab('documents');
      return;
    }
    if (config.requiredFields.some(f => !f.label.trim())) {
      setError('All question entries must have a label.');
      setActiveTab('fields');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await apiClient.put(`/admin/companies/${companyId}/manual-claim-config`, config);
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to save configuration.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }}>
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="bg-[#0f3057] px-8 py-6 flex items-start justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
              <FileText size={22} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Manual Claim Config</h2>
              <p className="text-blue-200 text-sm mt-0.5">{companyName}</p>
            </div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white transition-colors mt-1">
            <X size={22} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex px-8 border-b border-gray-100 flex-shrink-0 pt-4 bg-gray-50/50">
          <button
            onClick={() => setActiveTab('documents')}
            className={`pb-3 px-4 font-bold text-sm transition-colors border-b-2 ${
              activeTab === 'documents'
                ? 'border-[#0f3057] text-[#0f3057]'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            Required Documents
          </button>
          <button
            onClick={() => setActiveTab('fields')}
            className={`pb-3 px-4 font-bold text-sm transition-colors border-b-2 flex items-center gap-2 ${
              activeTab === 'fields'
                ? 'border-[#0f3057] text-[#0f3057]'
                : 'border-transparent text-gray-400 hover:text-gray-600'
            }`}
          >
            Questions / Dynamic Fields
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-8 py-6 space-y-4 bg-white">
          {loading ? (
            <div className="text-center py-12 text-gray-400">Loading configuration...</div>
          ) : activeTab === 'documents' ? (
            <>
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 flex items-start gap-3 mb-4">
                <AlertCircle size={18} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-amber-800">
                  Configure documents that customers must upload when filing an off-portal claim for {companyName}.
                </p>
              </div>

              {config.requiredDocuments.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400 font-medium">No document requirements configured.</p>
                </div>
              ) : (
                config.requiredDocuments.map((doc, index) => (
                  <div key={doc.id} className="flex items-center gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100 group">
                    <div className="w-7 h-7 rounded-full bg-[#0f3057] text-white text-xs font-black flex items-center justify-center flex-shrink-0">
                      {index + 1}
                    </div>
                    <input
                      type="text"
                      placeholder="e.g. Police Report"
                      className="flex-1 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium outline-none focus:ring-2 focus:ring-blue-500/20"
                      value={doc.name}
                      onChange={e => updateDocument(index, 'name', e.target.value)}
                    />
                    <button
                      type="button"
                      onClick={() => updateDocument(index, 'isRequired', !doc.isRequired)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all flex-shrink-0 ${
                        doc.isRequired
                          ? 'bg-red-50 text-red-600 border border-red-100'
                          : 'bg-gray-100 text-gray-400 border border-gray-200'
                      }`}
                    >
                      {doc.isRequired ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                      {doc.isRequired ? 'Required' : 'Optional'}
                    </button>
                    <button onClick={() => removeDocument(index)} className="text-gray-300 hover:text-red-500">
                      <Trash2 size={16} />
                    </button>
                  </div>
                ))
              )}
              
              <button
                onClick={addDocument}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gray-50 border border-dashed border-gray-300 text-gray-500 rounded-2xl text-sm font-bold hover:bg-gray-100 hover:text-gray-700 transition-colors"
              >
                <Plus size={16} /> Add Document Requirement
              </button>
            </>
          ) : (
            <>
              <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4 flex items-start gap-3 mb-4">
                <HelpCircle size={18} className="text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-800">
                  Configure specific questions or data fields you need the customer to answer when filing a manual claim.
                </p>
              </div>

              {config.requiredFields.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-400 font-medium">No questions configured.</p>
                </div>
              ) : (
                config.requiredFields.map((field, index) => (
                  <div key={field.id} className="flex flex-col gap-3 p-4 bg-gray-50 rounded-2xl border border-gray-100 group">
                    <div className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-black flex items-center justify-center flex-shrink-0">
                        {index + 1}
                      </div>
                      <input
                        type="text"
                        placeholder="e.g. Witness Name, Date of Incident..."
                        className="flex-1 px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium outline-none focus:ring-2 focus:ring-blue-500/20"
                        value={field.label}
                        onChange={e => updateField(index, 'label', e.target.value)}
                      />
                      <button onClick={() => removeField(index)} className="text-gray-300 hover:text-red-500">
                        <Trash2 size={16} />
                      </button>
                    </div>

                    <div className="flex items-center pl-10 gap-3">
                      <select
                        className="px-3 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold outline-none text-gray-600"
                        value={field.type}
                        onChange={e => updateField(index, 'type', e.target.value)}
                      >
                        <option value="text">Short Text</option>
                        <option value="textarea">Long Paragraph</option>
                        <option value="date">Date</option>
                        <option value="number">Number</option>
                      </select>

                      <button
                        type="button"
                        onClick={() => updateField(index, 'isRequired', !field.isRequired)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold transition-all flex-shrink-0 ${
                          field.isRequired
                            ? 'bg-red-50 text-red-600 border border-red-100'
                            : 'bg-gray-100 text-gray-400 border border-gray-200'
                        }`}
                      >
                        {field.isRequired ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                        {field.isRequired ? 'Required' : 'Optional'}
                      </button>
                    </div>
                  </div>
                ))
              )}

              <button
                onClick={addField}
                className="w-full flex items-center justify-center gap-2 px-5 py-3 bg-gray-50 border border-dashed border-gray-300 text-gray-500 rounded-2xl text-sm font-bold hover:bg-gray-100 hover:text-gray-700 transition-colors"
              >
                <Plus size={16} /> Add Question
              </button>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-gray-100 flex-shrink-0 bg-white space-y-3">
          {error && (
            <p className="text-sm text-red-600 flex items-center gap-2">
              <AlertCircle size={14} /> {error}
            </p>
          )}
          {saved && (
            <p className="text-sm text-emerald-600 flex items-center gap-2">
              <CheckCircle size={14} /> Configuration saved successfully!
            </p>
          )}
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 ml-auto">
              <button onClick={onClose} className="px-5 py-2.5 text-sm font-bold text-gray-500 hover:text-gray-700 transition-colors">
                Close
              </button>
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-6 py-2.5 bg-[#0f3057] text-white rounded-xl text-sm font-bold hover:bg-[#0a2040] transition-colors disabled:opacity-60"
              >
                <Save size={16} /> {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
